/**
 * `engine-pipeline` (`LN-WGT03`, the shape ruled in the `LN-JS-M0` review):
 * one line of code stepping through the engine's stages, left to right, with
 * what each stage holds.
 *
 * <div class="pc-widget" data-widget="engine-pipeline" data-code="return price * count;"
 *      data-steps="[{&quot;stage&quot;:&quot;Source text&quot;,&quot;show&quot;:&quot;return price * count;&quot;},
 *                   {&quot;stage&quot;:&quot;Tokens&quot;,&quot;show&quot;:[&quot;return&quot;,&quot;price&quot;,&quot;*&quot;,&quot;count&quot;,&quot;;&quot;],&quot;note&quot;:&quot;Five tokens.&quot;}]"
 *      data-caption="One line, every stage.">
 *   <p class="pc-widget-fallback">The line goes through the stages in order.</p>
 * </div>
 *
 * `data-code` (optional): the line, shown in the head. `data-steps`
 * (required): a JSON list of two or more `{ stage, show, note? }`: `stage`
 * names one box of the pipeline and is the readout, `show` what the stage
 * holds (a string is one code panel, a list one chip per item: tokens,
 * bytecode instructions), `note` one sentence in the lesson's language.
 * `data-caption` (optional). The first frame is the first stage with every
 * stage box drawn.
 */

import {
  attachStepper,
  buildShell,
  el,
  isNonEmptyString,
  isRecord,
  liveNote,
  parseJson,
  type WidgetText,
} from "./dom";

interface Stage {
  stage: string;
  show: string | string[];
  note?: string;
}

const isShow = (v: unknown): v is string | string[] =>
  typeof v === "string" || (Array.isArray(v) && v.length > 0 && v.every((item) => typeof item === "string"));

const isStages = (value: unknown): value is Stage[] =>
  Array.isArray(value) &&
  value.length >= 2 &&
  value.every(
    (step) =>
      isRecord(step) &&
      isNonEmptyString(step.stage) &&
      isShow(step.show) &&
      (step.note === undefined || typeof step.note === "string"),
  );

export function renderEnginePipeline(host: HTMLElement, text: WidgetText): boolean {
  const steps = parseJson(host.dataset.steps, isStages);
  if (!steps) return false;

  const shell = buildShell(host, {
    label: text("labelEngine"),
    code: host.dataset.code,
    caption: host.dataset.caption,
  });

  const strip = el("ol", "pc-pipeline");
  const boxes = steps.map((step) => {
    const box = el("li", "pc-pipeline-stage", step.stage);
    strip.append(box);
    return box;
  });
  const panel = el("div", "pc-pipeline-panel");
  const note = liveNote();
  shell.body.append(strip, panel, note);

  attachStepper(
    shell,
    steps.length,
    (index) => {
      boxes.forEach((box, i) => {
        box.classList.toggle("is-active", i === index);
        box.classList.toggle("is-done", i < index);
        if (i === index) box.setAttribute("aria-current", "step");
        else box.removeAttribute("aria-current");
      });
      const step = steps[index];
      panel.textContent = "";
      panel.append(el("span", "pc-pipeline-panel-label", step.stage));
      if (typeof step.show === "string") {
        panel.append(el("pre", "pc-pipeline-code", step.show));
      } else {
        const chips = el("div", "pc-pipeline-chips");
        for (const item of step.show) chips.append(el("code", "pc-pipeline-chip", item));
        panel.append(chips);
      }
      // The live region: a stage without a note is announced by its name.
      note.textContent = step.note ?? step.stage;
    },
    text,
    { liveReadout: false },
  );
  return true;
}
